import { type FC, type PropsWithChildren, type ReactElement, type ReactNode } from 'react';
import { type HoverEvents } from 'react-aria';
import cn from 'classnames';
import {
    Cell,
    HeaderCell,
    type HelpProps,
    Label,
    MiniGridRow,
    type TestProps,
    TextCell,
    type TextRenderFunction,
    VStack,
} from '@direct-frontend/components';
import { type Nullable } from '@direct-frontend/stdlib';
import { SettingsBlockRowStub } from './SettingsBlockRowStub';
import styles from './SettingsBlockRow.module.css';

export interface SettingsBlockRowContent {
    title: string | TextRenderFunction;
    caption?: string | TextRenderFunction;
    rightPadding?: boolean;
}

export type SettingsBlockRowProps = HoverEvents &
    TestProps &
    PropsWithChildren<{
        id: string;
        title: ReactNode;
        isStub?: boolean;
        content: Nullable<SettingsBlockRowContent>;
        contentAddonLeft?: ReactNode;
        tooltip?: string;
        headerClassName?: string;
        emptyLabel?: string;
        labelIcon?: ReactElement;
        withoutTopBorder?: boolean;
        isWrap?: boolean;
        help?: (helpProps: HelpProps) => ReactNode;
        onClick?: VoidFunction;
        isContentTruncated?: boolean;
        className?: string;
    }>;

export const SettingsBlockRow: FC<SettingsBlockRowProps> = ({
    id,
    title,
    isStub,
    isContentTruncated,
    content,
    contentAddonLeft,
    headerClassName,
    emptyLabel = 'Не указан',
    labelIcon,
    withoutTopBorder,
    children,
    isWrap = false,
    className,
    help,
    onHoverEnd,
    onHoverStart,
    onHoverChange,
    onClick,
    'data-testid': dataTestId = 'SettingsBlockRow',
}) => {
    if (isStub) {
        return <SettingsBlockRowStub id={id} />;
    }

    return (
        <MiniGridRow
            id={id}
            withoutTopBorder={withoutTopBorder}
            onClick={onClick}
            onHoverStart={onHoverStart}
            onHoverEnd={onHoverEnd}
            onHoverChange={onHoverChange}
            className={className}
            data-testid={`${dataTestId}.Row`}
        >
            <HeaderCell
                title={title}
                help={help}
                className={cn(headerClassName, styles.header)}
                isTitleTruncated={false}
                data-testid={dataTestId + '.HeaderCell'}
            />

            {content ? (
                <>
                    {contentAddonLeft && <Cell leftPadding={false}>{contentAddonLeft}</Cell>}
                    <TextCell
                        isTruncated={isContentTruncated}
                        title={content.title}
                        caption={content.caption}
                        leftPadding={false}
                        rightPadding={content.rightPadding ?? true}
                        width="full"
                        className={cn(
                            styles.content,
                            isWrap && styles.content_wrap,
                            isContentTruncated && styles.content_truncated
                        )}
                        data-testid={dataTestId + '.ContentCell'}
                    />
                </>
            ) : (
                <Cell width="full" leftPadding={false} alignItems="start" data-testid={dataTestId + '.ContentCell'}>
                    <VStack>
                        <Label size="3xs" addonLeft={labelIcon}>
                            {emptyLabel}
                        </Label>
                    </VStack>
                </Cell>
            )}

            {children}
        </MiniGridRow>
    );
};
